import React, { Dispatch } from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { RouteComponentProps } from "react-router";
import { showSnack } from "react-redux-snackbar";
import { ModalHeader, ModalBody, ModalFooter, Button, Modal } from "reactstrap";
import {
  IAppState,
  LoadingState,
  ResponseError,
  ErrorDisplay,
  LoadingDisplay,
  ForbiddenDisplay,
  ApiRequest,
  appPaths
} from "../../../Common";
import { endpoints } from "../../Constants/endpoints";
import { Workflow } from "../../Models/Workflow";
import {
  getWorkflow,
  unloadWorkflow,
  setLoading as setWorkflowLoading
} from "../../Actions/WorkflowActions";
import {
  getWorkflowOptions,
  setLoading as setWorkflowOptionsLoading
} from "../../Actions/WorkflowOptionActions";
import WorkflowEditorWrapper from "../Common/WorkflowEditorWrapper";
import ResponseHandler from "../../../App/ResponseHandler";
import { WorkflowOptions } from "../../Models/WorkflowOptions";

type TParams = {
  code: string;
};

type Props = {};

type StateProps = {
  // workflow stuff
  workflow?: Workflow;
  workflowLoadingState: LoadingState;
  workflowErrorMessage: string;
  workflowErrorCode?: number;

  // workflow options stuff
  workflowOptions?: WorkflowOptions;
  workflowOptionsLoadingState: LoadingState;
  workflowOptionsErrorMessage: string;
  workflowOptionsErrorCode?: number;
};

type DispatchProps = {
  getWorkflow: (code: string) => void;
  unloadWorkflow: () => void;
  setWorkflowLoading: () => void;
  getWorkflowOptions: () => void;
  setWorkflowOptionsLoading: () => void;
  showSnack: (uniqueId: any, args: any) => void;
};

type State = {
  isWorkflowEditorEnabled: boolean;
  showFeedbackDisplaceWarningModal: boolean;
  toBeSavedWorkflowJson?: string;
  numFeedbacksWillBeDisplaced?: number;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  workflow: state.workflow.workflow,
  workflowLoadingState: state.workflow.loadingState,
  workflowErrorMessage: state.workflow.errorMessage,
  workflowErrorCode: state.workflow.errorCode,
  workflowOptions: state.workflowOptions.options,
  workflowOptionsLoadingState: state.workflowOptions.loadingState,
  workflowOptionsErrorMessage: state.workflowOptions.errorMessage,
  workflowOptionsErrorCode: state.workflowOptions.errorCode
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  getWorkflow: (code: string) => dispatch(getWorkflow({ code })),
  unloadWorkflow: () => dispatch(unloadWorkflow({})),
  setWorkflowLoading: () => dispatch(setWorkflowLoading({})),
  getWorkflowOptions: () => dispatch(getWorkflowOptions({})),
  setWorkflowOptionsLoading: () => dispatch(setWorkflowOptionsLoading({})),
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
});

class EditWorkflowContainer extends React.Component<
  Props & StateProps & DispatchProps & RouteComponentProps<TParams>,
  State
> {
  constructor(props: Props & StateProps & DispatchProps & RouteComponentProps<TParams>) {
    super(props);

    this.state = { isWorkflowEditorEnabled: true, showFeedbackDisplaceWarningModal: false };

    this.onSaveWorkflowChanges = this.onSaveWorkflowChanges.bind(this);
    this.onCancelWorkflowEdit = this.onCancelWorkflowEdit.bind(this);
    this.feedbackDisplaceModalToggle = this.feedbackDisplaceModalToggle.bind(this);
    this.onFeedbackDisplaceModalContinue = this.onFeedbackDisplaceModalContinue.bind(this);
  }

  componentDidMount = () => {
    this.loadIfNeeded(this.props);
  };

  componentDidUpdate = (
    prevProps: Props & StateProps & DispatchProps & RouteComponentProps<TParams>
  ) => {
    this.loadIfNeeded(prevProps);
  };

  componentWillUnmount = () => {
    this.props.unloadWorkflow();
  };

  // this method is the callback from the workflow editor.
  onSaveWorkflowChanges(workflowJson: string) {
    // PUT changes to the server
    const that = this;
    this.setState(
      (prevState: State) => ({
        isWorkflowEditorEnabled: false
      }),
      () => {
        if (that.props.workflow) {
          // check to make sure no feedbacks will be displaced by the changes
          ApiRequest.fromPath(
            endpoints.checkFeedbackDisplacedByJsonUpdate(that.props.workflow.code)
          )
            .withAuthHeader()
            .withJson({
              workflowJson
            })
            .post()
            .then(response => {
              ResponseHandler.handle(
                response,
                result => {
                  // this was successful
                  if (result.payload > 0) {
                    // we need to warn the user about this save
                    this.setState((prevState: State) => ({
                      showFeedbackDisplaceWarningModal: true,
                      toBeSavedWorkflowJson: workflowJson,
                      numFeedbacksWillBeDisplaced: result.payload
                    }));
                  } else {
                    this.saveWorkflowChanges(workflowJson);
                  }
                },
                result => {
                  this.setState(
                    (prevState: State) => ({
                      isWorkflowEditorEnabled: true
                    }),
                    () => {
                      this.props.showSnack("CHECK_WORKFLOW_JSON", {
                        label: `Failed to check workflow json for feedback displacement: ${result.error.message}`,
                        timeout: 2000
                      });
                    }
                  );
                },
                () => {
                  this.setState(
                    (prevState: State) => ({
                      isWorkflowEditorEnabled: true
                    }),
                    () => {
                      this.props.showSnack("CHECK_WORKFLOW_JSON", {
                        label: `Failed to check workflow json for feedback displacement: An unknown error has occurred (${response.status})`,
                        timeout: 2000
                      });
                    }
                  );
                }
              );
            });
        }
      }
    );
  }

  onCancelWorkflowEdit() {
    // this.redirectBack();
  }

  onFeedbackDisplaceModalContinue(workflowJson: string) {
    this.setState(
      (prevState: State) => ({
        showFeedbackDisplaceWarningModal: false,
        toBeSavedWorkflowJson: undefined,
        numFeedbacksWillBeDisplaced: undefined
      }),
      () => {
        this.saveWorkflowChanges(workflowJson);
      }
    );
  }

  feedbackDisplaceModalToggle() {
    this.setState((prevState: State) => ({
      showFeedbackDisplaceWarningModal: false,
      toBeSavedWorkflowJson: undefined,
      numFeedbacksWillBeDisplaced: undefined,
      isWorkflowEditorEnabled: true
    }));
  }

  loadIfNeeded(prevProps: Props & StateProps & DispatchProps & RouteComponentProps<TParams>) {
    const { isLoading, isLoaded, errored } = this.props.workflowLoadingState;

    if (
      // case where we haven't started loading yet
      (!isLoading && !isLoaded && !errored) ||
      // case where we have gone to a page with a new code
      this.props.match.params.code !== prevProps.match.params.code
    ) {
      this.load();
    }
  }

  load() {
    // load the workflow
    this.props.setWorkflowLoading();
    this.props.getWorkflow(this.props.match.params.code);

    // load the workflow options (this includes stuff like permission and team options)
    this.props.setWorkflowOptionsLoading();
    this.props.getWorkflowOptions();
  }

  redirectBack() {
    if (!!this.props.workflow && this.props.workflow.isTemplate) {
      this.props.history.push(appPaths.topic.workflowTemplates);
    } else {
      this.props.history.push(appPaths.topic.root);
    }
  }

  saveWorkflowChanges(workflowJson: string) {
    if (this.props.workflow) {
      ApiRequest.fromPath(endpoints.updateJson(this.props.workflow.code))
        .withAuthHeader()
        .withJson({
          workflowJson
        })
        .put()
        .then(response => {
          ResponseHandler.handle(
            response,
            result => {
              // success, redirect back
              this.redirectBack();
            },
            result => {
              this.setState(
                (prevState: State) => ({
                  isWorkflowEditorEnabled: true
                }),
                () => {
                  this.props.showSnack("EDIT_WORKFLOW_JSON", {
                    label: `Failed to update workflow: ${result.error.message}`,
                    timeout: 2000
                  });
                }
              );
            },
            () => {
              this.setState(
                (prevState: State) => ({
                  isWorkflowEditorEnabled: true
                }),
                () => {
                  this.props.showSnack("EDIT_WORKFLOW_JSON", {
                    label: `Failed to update workflow: An unknown error has occurred (${response.status})`,
                    timeout: 2000
                  });
                }
              );
            }
          );
        });
    }
  }

  render() {
    const isLoading =
      this.props.workflowLoadingState.isLoading || this.props.workflowOptionsLoadingState.isLoading;
    const isLoaded =
      this.props.workflowLoadingState.isLoaded && this.props.workflowOptionsLoadingState.isLoaded;
    const errored =
      this.props.workflowLoadingState.errored || this.props.workflowOptionsLoadingState.errored;

    if (errored) {
      return (
        <ErrorDisplay
          errorCode={this.props.workflowErrorCode as number}
          errorDescription={this.props.workflowErrorMessage}
          errorResult="Failed to get Workflow"
        />
      );
    }

    if (isLoading) {
      return <LoadingDisplay />;
    }

    if (isLoaded && !!this.props.workflow && !!this.props.workflowOptions) {
      // check permissions
      if (!this.props.workflow.permissions.canView || !this.props.workflow.permissions.canEdit) {
        return <ForbiddenDisplay description="You do not have permission to edit this workflow" />;
      }

      // at this point we are all good to render the editor
      return (
        <>
          <WorkflowEditorWrapper
            workflow={this.props.workflow}
            onSave={this.onSaveWorkflowChanges}
            onCancel={this.onCancelWorkflowEdit}
            permissions={this.props.workflowOptions.permissionOptions}
            teams={this.props.workflowOptions.teamOptions}
            isLoading={!this.state.isWorkflowEditorEnabled}
          />
          <Modal
            isOpen={this.state.showFeedbackDisplaceWarningModal}
            toggle={this.feedbackDisplaceModalToggle}
            color="warning"
          >
            <ModalHeader toggle={this.feedbackDisplaceModalToggle}>
              Warning: {this.state.numFeedbacksWillBeDisplaced} Feedbacks will be Reset
            </ModalHeader>
            <ModalBody>
              <p>
                States which feedbacks are assigned to have been removed. If this change is saved,
                those Feedbacks will be reset to the Start state of this workflow.
              </p>
              <p>Do you want to continue?</p>
            </ModalBody>
            <ModalFooter>
              <Button
                color="danger"
                onClick={() => {
                  this.onFeedbackDisplaceModalContinue(this.state.toBeSavedWorkflowJson as string);
                }}
              >
                Continue
              </Button>{" "}
              <Button color="secondary" onClick={this.feedbackDisplaceModalToggle}>
                Cancel
              </Button>
            </ModalFooter>
          </Modal>
        </>
      );
    }

    return <></>;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(EditWorkflowContainer);
